import React, { useState, useEffect, useCallback } from "react";
import { apiService } from "@/api/api";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, Plus, ChevronLeft, ChevronRight, User, Stethoscope, History, 
  ArrowLeft, FileText, Edit, Download, Calendar, TrendingUp, AlertTriangle, 
  Pill, Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp,
  Activity, Brain, Heart, Target, Award
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AddClientDialog from "@/components/clients/AddClientDialog";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import type { Client } from "@/types/client";
import type { Session } from "@/types/session";

const Clients = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionSearchTerm, setSessionSearchTerm] = useState("");
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientsData, setClientsData] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [clientSessions, setClientSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [activeTab, setActiveTab] = useState("sessions");
  const [expandedSections, setExpandedSections] = useState({
    executive: true,
    assessment: false,
    interventions: false,
    timeline: false,
    recommendations: false
  });
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [updateLoading, setUpdateLoading] = useState(false);

  const filteredClients = Array.isArray(clientsData) 
    ? clientsData.filter((client) =>
        `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      ) 
    : [];

  const filteredSessions = clientSessions.filter((session) =>
    (session.session_title || "Session").toLowerCase().includes(sessionSearchTerm.toLowerCase())
  );

  const calculateAge = (dob: string | undefined): number | null => {
    if (!dob) return null;
    const dobDate = new Date(dob);
    if (isNaN(dobDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return age;
  };

  const normalizeClient = useCallback((apiClient: any): Client => ({
    client_id: apiClient.client_id,
    first_name: apiClient.first_name || '',
    last_name: apiClient.last_name || '',
    phone_number: apiClient.phone_number || '',
    status: apiClient.status || 'Active',
    primary_diagnosis: apiClient.primary_diagnosis || '',
    other_diagnoses: apiClient.other_diagnoses || [],
    high_risk: apiClient.high_risk || false,
    other_client_factors: apiClient.other_client_factors || '',
    gender: apiClient.gender || 'Other',
    session_count: apiClient.session_count || 0,
    email: apiClient.email || '',
    date_of_birth: apiClient.date_of_birth || undefined, // ✅ Include DOB
  }), []);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getClients();
      console.log("API Response received for clients", response);
      let clientsArray: any[] = [];
      if (response && response.success && response.data) {
        if (Array.isArray(response.data)) {
          clientsArray = response.data;
        } else {
          const possibleClients = response.data.clients || Object.values(response.data).find(value => Array.isArray(value));
          if (Array.isArray(possibleClients)) {
              clientsArray = possibleClients;
          } else {
              console.log("No clients array found in response.data object");
              clientsArray = [];
          }
        }
      } else if (Array.isArray(response)) {
        clientsArray = response;
      } else if (response && Array.isArray(response.clients)) {
        clientsArray = response.clients;
      } else {
        console.warn("Unhandled API response structure for getClients:", response);
        clientsArray = [];
      }
      console.log("Extracted clients array:", clientsArray);
      const normalizedClients = clientsArray.map(normalizeClient);
      setClientsData(normalizedClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast({
        title: "Error",
        description: "Failed to fetch clients.",
        variant: "destructive",
      });
      setClientsData([]);
    } finally {
      setLoading(false);
    }
  }, [toast, normalizeClient]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (selectedClient?.client_id) {
      setLoadingSessions(true);
      console.log("Selected client ID for session fetch:", selectedClient.client_id);
      apiService.getClientSessions(selectedClient.client_id)
        .then(response => {
          console.log("API Response for client sessions:", response);
          let sessions = [];
          if (response?.data && Array.isArray(response.data)) {
            sessions = response.data;
          } else if (Array.isArray(response)) {
            sessions = response;
          } else if (response?.sessions && Array.isArray(response.sessions)) {
            sessions = response.sessions;
          } else if (response?.data?.sessions && Array.isArray(response.data.sessions)) {
            sessions = response.data.sessions;
          }
          const sortedSessions = sessions.sort((a, b) =>
            new Date(b.session_timestamp).getTime() - new Date(a.session_timestamp).getTime()
          );
          console.log("Extracted and sorted sessions:", sortedSessions);
          setClientSessions(sortedSessions);
        })
        .catch(error => {
          console.error("Error fetching client sessions:", error);
          toast({
            title: "Error",
            description: "Failed to fetch client sessions",
            variant: "destructive",
          });
          setClientSessions([]);
        })
        .finally(() => {
          setLoadingSessions(false);
        });
    } else {
      setClientSessions([]);
    }
  }, [selectedClient?.client_id, toast]);

  const fetchReportData = useCallback(async () => {
    if (!selectedClient?.client_id) return;
    setLoadingReport(true);
    try {
      console.log('Fetching latest client report for client:', selectedClient.client_id);
      const response = await apiService.getLatestClientReport(selectedClient.client_id);
      console.log('Latest Client Report Response:', response);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching client report:', error);
      toast({
        title: "Notice",
        description: "Could not load latest report data.",
        variant: "default",
      });
    } finally {
      setLoadingReport(false);
    }
  }, [selectedClient?.client_id, toast]);

  useEffect(() => {
    if (activeTab === "summary" && selectedClient?.client_id && !reportData) {
      fetchReportData();
    }
  }, [activeTab, selectedClient?.client_id, reportData, fetchReportData]);

  // Inline editing functions
  const handleEditClick = (fieldName: string, currentValue: string) => {
    setEditingField(fieldName);
    setEditingValue(currentValue);
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const handleEditSave = async (fieldName: string) => {
    if (!selectedClient || !editingValue.trim()) return;
    setUpdateLoading(true);
    try {
      const updateData: Partial<Client> = {};
      switch (fieldName) {
        case 'first_name':
          const [firstName, ...lastNameParts] = editingValue.trim().split(' ');
          updateData.first_name = firstName;
          if (lastNameParts.length > 0) {
            updateData.last_name = lastNameParts.join(' ');
          }
          break;
        case 'last_name':
          updateData.last_name = editingValue.trim();
          break;
        case 'email':
          updateData.email = editingValue.trim();
          break;
        case 'phone_number':
          updateData.phone_number = editingValue.trim();
          break;
        case 'gender':
          updateData.gender = editingValue.trim();
          break;
        case 'primary_diagnosis':
          updateData.primary_diagnosis = editingValue.trim();
          break;
        case 'date_of_birth': // ✅ Handle DOB
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (editingValue && !dateRegex.test(editingValue)) {
            toast({
              title: "Invalid Date",
              description: "Please enter a valid date in YYYY-MM-DD format.",
              variant: "destructive",
            });
            return;
          }
          updateData.date_of_birth = editingValue || undefined;
          break;
      }
      const updatedClient = await apiService.updateClient(selectedClient.client_id, updateData);
      setSelectedClient({ ...selectedClient, ...updateData });
      setClientsData(prev => 
        prev.map(client => 
          client.client_id === selectedClient.client_id 
            ? { ...client, ...updateData }
            : client
        )
      );
      toast({
        title: "Success",
        description: "Client information updated successfully",
      });
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: "Error",
        description: "Failed to update client information",
        variant: "destructive",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // Editable field component
  const EditableField = ({ 
    fieldName, 
    value, 
    placeholder = "Enter value...", 
    type = "text",
    className = ""
  }: {
    fieldName: string;
    value: string;
    placeholder?: string;
    type?: string;
    className?: string;
  }) => {
    const isEditing = editingField === fieldName;
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <Input
            type={type}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            placeholder={placeholder}
            className={`text-sm ${className}`}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEditSave(fieldName);
              } else if (e.key === 'Escape') {
                handleEditCancel();
              }
            }}
          />
          <div className="flex space-x-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEditSave(fieldName)}
              disabled={updateLoading}
              className="h-6 w-6 p-0"
            >
              <CheckCircle className="h-3 w-3 text-green-600" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleEditCancel}
              disabled={updateLoading}
              className="h-6 w-6 p-0"
            >
              <XCircle className="h-3 w-3 text-red-600" />
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between">
        <span className={`font-medium text-sm text-gray-900 ${className}`}>
          {value || "Not provided"}
        </span>
        <Edit 
          className="h-3 w-3 text-gray-400 cursor-pointer hover:text-gray-600" 
          onClick={() => handleEditClick(fieldName, value)}
        />
      </div>
    );
  };

  const handleAddClient = (newClient: Client) => {
    toast({
      title: "Success",
      description: "New client added successfully",
    });
    fetchClients();
    setSelectedClient(false);
  };

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setActiveTab("sessions");
    setReportData(null);
  };

  const handleSessionClick = (session: Session) => {
    navigate(`/session/${session.session_id}`, {
      state: {
        clientName: `${selectedClient?.first_name} ${selectedClient?.last_name}`,
        sessionDate: formatSessionDate(session.session_timestamp)
      }
    });
  };

  const formatSessionDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn("Invalid date string provided:", dateString);
        return "Invalid Date";
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Error Date";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getConfidenceColor = (confidence) => {
    const numConfidence = parseInt(confidence);
    if (numConfidence >= 85) return 'bg-green-100 text-green-800 border-green-200';
    if (numConfidence >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Summary Report Components
  const CitationBadge = ({ citations }) => {
    if (!citations || citations.length === 0) return null;
    return (
      <div className="flex gap-1 mt-2">
        {citations.map((citation, index) => (
          <Badge key={index} variant="outline" className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
            Ref {citation}
          </Badge>
        ))}
      </div>
    );
  };

  const ConfidenceIndicator = ({ confidence }) => {
    const numConfidence = parseInt(confidence);
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${numConfidence >= 85 ? 'bg-green-500' : numConfidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${numConfidence}%` }}
          ></div>
        </div>
        <Badge variant="outline" className={`text-xs ${getConfidenceColor(confidence)}`}>
          {confidence}
        </Badge>
      </div>
    );
  };

  const CollapsibleSection = ({ title, content, confidence, citations, isExpanded, onToggle, icon: Icon }) => (
    <Card className="shadow-md border border-gray-200 mb-4">
      <div>
        <button 
          onClick={onToggle}
          className="w-full text-left"
        >
          <CardHeader className="bg-white p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Icon className="h-5 w-5 text-gray-600" />
                {title}
              </CardTitle>
              <div className="flex items-center gap-3">
                <ConfidenceIndicator confidence={confidence} />
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </button>
        {isExpanded && (
          <CardContent className="bg-white p-4 pt-0 border-t border-gray-100">
            <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
              {content?.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3 last:mb-0">{paragraph}</p>
              ))}
            </div>
            <CitationBadge citations={citations} />
          </CardContent>
        )}
      </div>
    </Card>
  );

  const RecommendationsList = ({ content, confidence, citations }) => {
    const recommendations = content?.split(/\d+\./).filter(item => item.trim()).map(item => item.trim()) || [];
    return (
      <Card className="shadow-md border border-gray-200">
        <CardHeader className="bg-white p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Target className="h-5 w-5 text-gray-600" />
              Treatment Plan & Recommendations
            </CardTitle>
            <ConfidenceIndicator confidence={confidence} />
          </div>
        </CardHeader>
        <CardContent className="bg-white p-4 pt-0">
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{rec}</p>
              </div>
            ))}
          </div>
          <CitationBadge citations={citations} />
        </CardContent>
      </Card>
    );
  };

  const TimelineCard = ({ content, confidence, citations }) => {
    const timelineItems = content?.split('\n- ').map(item => item.replace(/^- /, '').trim()).filter(item => item) || [];
    return (
      <Card className="shadow-md border border-gray-200">
        <CardHeader className="bg-white p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600" />
              Treatment Journey Timeline
            </CardTitle>
            <ConfidenceIndicator confidence={confidence} />
          </div>
        </CardHeader>
        <CardContent className="bg-white p-4 pt-0">
          <div className="space-y-4">
            {timelineItems.map((item, index) => {
              const [period, description] = item.split(': ');
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    {index < timelineItems.length - 1 && <div className="w-0.5 h-8 bg-gray-300 mt-2"></div>}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="font-medium text-sm text-gray-800">{period}</div>
                    <div className="text-sm text-gray-600 mt-1">{description}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <CitationBadge citations={citations} />
        </CardContent>
      </Card>
    );
  };

  const InterventionsCard = ({ content, confidence, citations }) => {
    const sections = content?.split('\n') || [];
    const currentInterventions = sections.find(s => s.includes('Current interventions'))?.split('\n').slice(1) || [];
    const adherenceText = sections.find(s => s.includes('Adherence:')) || '';
    const progressText = sections.find(s => s.includes('Progress:')) || '';
    return (
      <Card className="shadow-md border border-gray-200">
        <CardHeader className="bg-white p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Activity className="h-5 w-5 text-gray-600" />
              Current Interventions & Adherence
            </CardTitle>
            <ConfidenceIndicator confidence={confidence} />
          </div>
        </CardHeader>
        <CardContent className="bg-white p-4 pt-0">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Active Treatments</h4>
              <div className="space-y-2">
                {currentInterventions.map((intervention, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                    <Pill className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-700">{intervention.replace(/^\d+\.\s*/, '')}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Adherence Status</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-700">Medication compliance: Good</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-700">Session attendance: Regular</span>
                </div>
              </div>
              {progressText && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-1 text-sm">Progress Notes</h5>
                  <p className="text-sm text-gray-600">{progressText.replace('Progress:', '').trim()}</p>
                </div>
              )}
            </div>
          </div>
          <CitationBadge citations={citations} />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
      <div className="p-3 md:p-6 w-full">
        <Navbar />
        <main className="flex-1 overflow-y-auto mt-4 md:mt-6">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {!selectedClient && (
              <>
                <div className="flex justify-between items-center mb-4 md:mb-6">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Clients</h1>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm md:text-base px-3 md:px-4 py-2 h-9 md:h-10 rounded-md"
                    onClick={() => setAddClientOpen(true)}
                  >
                    <Plus size={14} className="mr-2 md:w-4 md:h-4" />
                    Add New Client
                  </Button>
                </div>
                <div className="flex mb-4 md:mb-6 gap-3 md:gap-4 flex-col sm:flex-row">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      placeholder="Search client name..."
                      className="pl-9 h-10 md:h-auto text-sm md:text-base border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button variant="outline" className="h-10 md:h-auto text-sm md:text-base border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md">Filter</Button>
                </div>
              </>
            )}
            {selectedClient ? (
              <div>
                <div className="mb-4 md:mb-6 flex items-center">
                  <Button
                    variant="ghost"
                    className="mr-2 p-0 h-auto hover:bg-transparent text-gray-600 hover:text-gray-900"
                    onClick={() => setSelectedClient(null)}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                    {selectedClient.first_name} {selectedClient.last_name}
                  </h2>
                </div>
                {/* Client Information Header */}
                <Card className="mb-6 shadow-md border border-gray-200">
                  <CardHeader className="bg-white p-6 pb-4 border-b border-gray-200">
                    <CardTitle className="text-lg md:text-xl font-semibold text-gray-800 flex items-center space-x-2">
                      <User className="h-5 w-5 text-gray-600" />
                      <span>Client Details</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Personal Section */}
                      <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-semibold text-gray-700">Personal</h3>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Full Name</p>
                              <EditableField
                                fieldName="first_name"
                                value={`${selectedClient.first_name} ${selectedClient.last_name}`}
                                placeholder="Enter full name"
                              />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Email</p>
                              <EditableField
                                fieldName="email"
                                value={selectedClient.email || ""}
                                placeholder="Enter email address"
                                type="email"
                              />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Phone</p>
                              <EditableField
                                fieldName="phone_number"
                                value={selectedClient.phone_number || ""}
                                placeholder="Enter phone number"
                                type="tel"
                              />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Gender</p>
                              <EditableField
                                fieldName="gender"
                                value={selectedClient.gender || ""}
                                placeholder="Enter gender"
                              />
                            </div>
                            {/* ✅ Replaced Status with Age */}
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Age</p>
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-gray-900">
                                  {selectedClient.date_of_birth
                                    ? `${calculateAge(selectedClient.date_of_birth)} years`
                                    : "Not provided"}
                                </span>
                                <Edit 
                                  className="h-3 w-3 text-gray-400 cursor-pointer hover:text-gray-600" 
                                  onClick={() => handleEditClick('date_of_birth', selectedClient.date_of_birth || '')}
                                />
                              </div>

                              {editingField === 'date_of_birth' && (
                                <div className="mt-2">
                                  <Input
                                    type="date"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    className="w-full text-sm p-2 border border-gray-300 rounded"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleEditSave('date_of_birth');
                                      } else if (e.key === 'Escape') {
                                        handleEditCancel();
                                      }
                                    }}
                                  />
                                  <div className="flex space-x-2 mt-2">
                                    <Button
                                      size="sm"
                                      onClick={() => handleEditSave('date_of_birth')}
                                      disabled={updateLoading}
                                      className="h-6 px-2 text-xs"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={handleEditCancel}
                                      disabled={updateLoading}
                                      className="h-6 px-2 text-xs"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                      </div>
                      {/* Diagnosis Section */}
                      <div>
                        <h3 className="text-base font-semibold text-gray-700 mb-4">Diagnosis</h3>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              {editingField === 'primary_diagnosis' ? (
                                <div className="flex-1 flex items-center space-x-2">
                                  <Input
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    placeholder="Enter primary diagnosis"
                                    className="text-sm flex-1"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleEditSave('primary_diagnosis');
                                      } else if (e.key === 'Escape') {
                                        handleEditCancel();
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditSave('primary_diagnosis')}
                                    disabled={updateLoading}
                                    className="h-6 w-6 p-0"
                                  >
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleEditCancel}
                                    disabled={updateLoading}
                                    className="h-6 w-6 p-0"
                                  >
                                    <XCircle className="h-3 w-3 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <span className="font-medium text-sm text-gray-900">
                                    {selectedClient.primary_diagnosis || "No diagnosis specified"}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <Search className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                                    <Edit 
                                      className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" 
                                      onClick={() => handleEditClick('primary_diagnosis', selectedClient.primary_diagnosis || '')}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Risk Assessment</span>
                              <AlertTriangle className="h-4 w-4 text-gray-400" />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700"></span>
                              <Plus className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Therapy Focus</span>
                              <div className="flex space-x-1">
                                <div className="w-1 h-4 bg-gray-300 rounded"></div>
                                <div className="w-1 h-4 bg-gray-300 rounded"></div>
                                <div className="w-1 h-4 bg-gray-300 rounded"></div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700"></span>
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Overview & Summary Section */}
                      <div>
                        <h3 className="text-base font-semibold text-gray-700 mb-4">Summary</h3>
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-gray-500">Total Sessions</p>
                            <p className="text-2xl font-bold text-gray-900">{clientSessions.length}</p>
                          </div>
                          {/* Clinical Overview */}
                          {reportData?.metadata?.overallSummary && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                              <h4 className="font-medium text-gray-700 mb-2 text-sm">Clinical Overview</h4>
                              <p className="text-xs text-gray-700 leading-relaxed">
                                {reportData.metadata.overallSummary.length > 200 
                                  ? `${reportData.metadata.overallSummary.substring(0, 200)}...`
                                  : reportData.metadata.overallSummary
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {/* Custom Tabs Implementation */}
                <div className="w-full">
                  <div className="flex border-b border-gray-200 bg-gray-50 p-1 rounded-t-lg mb-0">
                    <button
                      onClick={() => setActiveTab("sessions")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all duration-200 rounded-md flex-1 ${
                        activeTab === "sessions" 
                          ? "bg-white shadow-sm border-b-2 border-blue-500 text-blue-600" 
                          : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      <History className="h-4 w-4" />
                      Sessions ({clientSessions.length})
                    </button>
                    <button
                      onClick={() => setActiveTab("summary")}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all duration-200 rounded-md flex-1 ${
                        activeTab === "summary" 
                          ? "bg-white shadow-sm border-b-2 border-blue-500 text-blue-600" 
                          : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      <Stethoscope className="h-4 w-4" />
                      Clinical Summary
                    </button>
                  </div>
                  {activeTab === "sessions" && (
                    <Card className="shadow-md border border-gray-200 rounded-t-none border-t-0">
                      <CardHeader className="bg-white p-6 pb-4 border-b border-gray-200">
                        <CardTitle className="text-lg md:text-xl font-semibold text-gray-800 flex items-center space-x-2">
                          <History className="h-5 w-5 text-gray-600" />
                          <span>Session History ({clientSessions.length} sessions)</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="bg-white p-6">
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                          <Input
                            placeholder="Search sessions..."
                            className="pl-9 h-10 md:h-auto text-sm md:text-base rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            value={sessionSearchTerm}
                            onChange={(e) => setSessionSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="space-y-3">
                          {loadingSessions ? (
                            <div className="space-y-2">
                              <Skeleton className="h-16 w-full rounded-lg" />
                              <Skeleton className="h-16 w-full rounded-lg" />
                              <Skeleton className="h-16 w-full rounded-lg" />
                            </div>
                          ) : filteredSessions.length > 0 ? (
                            filteredSessions.map((session) => (
                              <div
                                key={session.session_id}
                                className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100 cursor-pointer flex justify-between items-center gap-3 transition-colors"
                                onClick={() => handleSessionClick(session)}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-base md:text-lg text-gray-900 truncate">{session.session_title || "Session"}</p>
                                  <p className="text-xs md:text-sm text-gray-600 truncate mb-1">
                                    {formatSessionDate(session.session_timestamp)} {session.session_type || "N/A"}
                                  </p>
                                  <Badge
                                    className={`text-xs rounded-full font-medium px-2 py-0.5 ${session.session_status === "completed" ? "bg-green-100 text-green-800 border-green-200" : "bg-yellow-100 text-yellow-800 border-yellow-200"}`}
                                  >
                                    {session.session_status}
                                  </Badge>
                                </div>
                                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0" />
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 py-4 text-base text-center">
                              {sessionSearchTerm ? "No sessions found matching your search." : "No sessions recorded for this client."}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {activeTab === "summary" && (
                    <>
                      {loadingReport ? (
                        <Card className="shadow-md border border-gray-200 rounded-t-none border-t-0">
                          <CardContent className="bg-white p-12">
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                            </div>
                          </CardContent>
                        </Card>
                      ) : !reportData ? (
                        <Card className="shadow-md border border-gray-200 rounded-t-none border-t-0">
                          <CardContent className="bg-white p-12">
                            <div className="text-center">
                              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Report Data Available</h3>
                              <p className="text-gray-600 mb-4">Unable to load the clinical summary report for this client.</p>
                              <Button onClick={fetchReportData}>Try Again</Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg shadow-md">
                          <div className="p-6">
                            <div className="space-y-6">
                              {/* Report Quality & Status Dashboard */}
                              <Card className="shadow-lg border border-gray-200">
                                <CardHeader className="bg-white p-6 pb-4">
                                  <CardTitle className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <Award className="h-5 w-5 text-gray-600" />
                                    Report Quality & Overview
                                  </CardTitle>
                                </CardHeader>
                                <CardContent className="bg-white p-6 pt-0">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                                      <div className="text-3xl font-bold text-blue-600 mb-1">
                                        {reportData.metadata.overallConfidence}
                                      </div>
                                      <div className="text-sm text-gray-600">Overall Confidence</div>
                                    </div>
                                    <div className="text-center p-4 bg-green-50 rounded-lg">
                                      <div className="text-lg font-bold text-green-600 mb-1 flex items-center justify-center gap-1">
                                        <CheckCircle className="h-5 w-5" />
                                        {reportData.metadata.status}
                                      </div>
                                      <div className="text-sm text-gray-600">Report Status</div>
                                    </div>
                                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                                      <div className="text-3xl font-bold text-gray-600 mb-1">
                                        {reportData.metadata.overallErrors?.length || 0}
                                      </div>
                                      <div className="text-sm text-gray-600">Processing Errors</div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              {/* Clinical Sections */}
                              <div className="space-y-6">
                                {reportData.sections.executive_summary && (
                                  <CollapsibleSection
                                    title="Executive Summary"
                                    content={reportData.sections.executive_summary.content}
                                    confidence={reportData.sections.executive_summary.confidence}
                                    citations={reportData.sections.executive_summary.citations}
                                    isExpanded={expandedSections.executive}
                                    onToggle={() => toggleSection('executive')}
                                    icon={User}
                                  />
                                )}
                                {reportData.sections.assessment_formulation && (
                                  <CollapsibleSection
                                    title="Clinical Assessment & Formulation"
                                    content={reportData.sections.assessment_formulation.content}
                                    confidence={reportData.sections.assessment_formulation.confidence}
                                    citations={reportData.sections.assessment_formulation.citations}
                                    isExpanded={expandedSections.assessment}
                                    onToggle={() => toggleSection('assessment')}
                                    icon={Brain}
                                  />
                                )}
                                {reportData.sections.interventions_adherence && (
                                  <InterventionsCard
                                    content={reportData.sections.interventions_adherence.content}
                                    confidence={reportData.sections.interventions_adherence.confidence}
                                    citations={reportData.sections.interventions_adherence.citations}
                                  />
                                )}
                                {reportData.sections.journey_timeline && (
                                  <TimelineCard
                                    content={reportData.sections.journey_timeline.content}
                                    confidence={reportData.sections.journey_timeline.confidence}
                                    citations={reportData.sections.journey_timeline.citations}
                                  />
                                )}
                                {reportData.sections.plan_recommendations && (
                                  <RecommendationsList
                                    content={reportData.sections.plan_recommendations.content}
                                    confidence={reportData.sections.plan_recommendations.confidence}
                                    citations={reportData.sections.plan_recommendations.citations}
                                  />
                                )}
                              </div>
                              {/* Action Buttons */}
                              <div className="flex justify-center space-x-4 mt-12 pt-8 border-t border-gray-200">
                                <Button
                                  onClick={() => window.print()}
                                  variant="outline"
                                  className="flex items-center space-x-2 text-blue-600 border border-blue-300 hover:bg-blue-50 px-6 py-2 rounded-md font-medium"
                                >
                                  <Download className="h-4 w-4" />
                                  <span>Print / Export Report</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8 md:py-12">
                    <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500 text-sm md:text-base">Loading client information...</p>
                  </div>
                ) : filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <div
                      key={client.client_id}
                      className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-gray-200 transition-all hover:shadow-md cursor-pointer"
                      onClick={() => handleClientClick(client)}
                    >
                      <div className="flex items-start justify-between flex-col sm:flex-row gap-3">
                        <div className="flex-1 min-w-0 w-full">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1 truncate">{client.first_name} {client.last_name}</h3>
                          <div className="flex gap-2 text-xs md:text-sm text-gray-500 mb-2 flex-wrap">
                            <p>Sessions: {client.session_count !== undefined ? client.session_count : 0}</p>
                            <span className="hidden sm:inline-block text-gray-400"></span>
                            <p className="truncate">Primary Dx: {client.primary_diagnosis || "None"}</p>
                          </div>
                          <div className="flex gap-2 flex-wrap mb-2">
                            <Badge className={`text-xs rounded-full font-medium ${client.status === "Active" ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-700 border-gray-300"}`}>
                              {client.status}
                            </Badge>
                            <Badge className={`text-xs rounded-full font-medium ${client.high_risk ? "bg-red-100 text-red-800 border-red-200" : "bg-blue-100 text-blue-700 border-blue-200"}`}>
                              {client.high_risk ? "High Risk" : (client.gender || "N/A")}
                            </Badge>
                          </div>
                          {client.other_diagnoses && client.other_diagnoses.length > 0 && (
                            <div className="text-xs text-gray-600 mt-1 truncate">Other Dx: {client.other_diagnoses.join(", ")}</div>
                          )}
                          {client.other_client_factors && (
                            <div className="text-xs text-gray-600 mt-1 truncate">Other Factors: {client.other_client_factors}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                          <div className="text-left sm:text-right mr-2 md:mr-4 flex-1 sm:flex-initial">
                            <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">Gender</div>
                            <div className="text-xs md:text-sm text-gray-600">{client.gender || "N/A"}</div>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8 md:h-10 md:w-10 flex-shrink-0 text-blue-600 hover:bg-blue-50">
                            <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 md:py-12 px-4">
                    <p className="text-gray-500 text-sm md:text-base">
                      {searchTerm ? "No clients found matching your search" : "No clients found. Click 'Add New Client' to get started."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      <AddClientDialog
        open={addClientOpen}
        onOpenChange={setAddClientOpen}
        onSave={handleAddClient}
      />
    </div>
  );
};

export default Clients;